import _ from "lodash";
import { handleApiAction } from "utils/actions";
import { apiClient } from "utils/clients"
import externals from "utils/externals";
import axios from "axios";
import { isNcl } from "utils/app/utils";
import { cancelActiveNavigation } from "./consoleObjectNavigation";
import {
    CONSOLE_OBJECTS_PREVIEW_LIMIT,
    CONSOLE_OBJECTS_TYPE_SEE_ALL_LIMIT,
    DEFAULT_ONTAP_OBJECT_TYPES,
    getConsoleObjectsQuery,
    getDocumentationSearchQuery,
    getKnowledgeBaseSearchQuery,
    mapUnifiedSearchResponse
} from "./consoleObjects";
import {
    globalSearchResultCategory,
    hasGlobalSearchResults,
    matchTypeForLabel,
    recordGlobalSearchQuery,
    trackGlobalSearchAbandoned,
    trackGlobalSearchOpened,
    trackGlobalSearchResultClicked
} from "./globalSearchFullStory";

const getConsoleObjectsUrl = organizationId =>
    `${externals.searchBackend}/organizations/${organizationId}/searchResults`;

let documentationAbortController = null;
let knowledgeBaseAbortController = null;
let consoleObjectsAbortController = null;
let seeAllAbortController = null;

const abortInFlightConsoleObjectRequests = () => {
    if (consoleObjectsAbortController) {
        consoleObjectsAbortController.abort();
        consoleObjectsAbortController = null;
    }
    if (seeAllAbortController) {
        seeAllAbortController.abort();
        seeAllAbortController = null;
    }
};

const abortReferenceSearches = () => {
    if (documentationAbortController) {
        documentationAbortController.abort();
        documentationAbortController = null;
    }
    if (knowledgeBaseAbortController) {
        knowledgeBaseAbortController.abort();
        knowledgeBaseAbortController = null;
    }
};

const fetchSearchResults = async (organizationId, params, readController, writeController) => {
    const current = readController();
    if (current) {
        current.abort();
    }
    const controller = new AbortController();
    writeController(controller);
    const release = () => {
        if (readController() === controller) {
            writeController(null);
        }
    };

    try {
        const ans = await apiClient.get(getConsoleObjectsUrl(organizationId), {
            params,
            signal: controller.signal
        });
        release();
        return ans;
    } catch (error) {
        release();
        if (axios.isCancel(error)) {
            return { data: "duplicate call" };
        }
        throw error;
    }
};

const seeAllResultCount = fulfilled => fulfilled.reduce((total, item) => total + (_.isNumber(item.count) ? item.count : item.results.length), 0);

const dispatchMissingOrganization = (dispatch, getState, action) => {
    dispatch({
        type: `${action.type}-ERROR`,
        payload: new Error("Organization is not selected"),
        request: action,
        requestId: getState().globalSearch.consoleObjects?.requestId
    });
};

export default ({ dispatch, getState }) => next => async action => {
    const { type, payload } = action;
    const searchBefore = getState().globalSearch;
    const response = next(action);

    if (type === "GLOBAL-SEARCH/OPEN") {
        trackGlobalSearchOpened({
            inputMethod: action.inputMethod,
            page: window.location?.pathname || ""
        });
        if (getState().globalSearch.isInitialized) {
            return response;
        } else {
            const servicesList = getState().app.servicesList;
            const featuresByKey = getState().features.byKey;
            const isRestrictedEnv = !getState().tenancy.selectedAccount.isSaas;
            const navJson = getState().app.navJson;
            dispatch({
                type: "GLOBAL-SEARCH/INITIALIZE",
                payload: {
                    servicesList,
                    featuresByKey,
                    navJson,
                    isRestrictedEnv
                }
            })
        }
    } else if (type === "GLOBAL-SEARCH/CLEAR" || type === "GLOBAL-SEARCH/CLOSE" || type === "GLOBAL-SEARCH/QUERY-TOO-SHORT"){
        if (type === "GLOBAL-SEARCH/CLOSE") {
            trackGlobalSearchAbandoned({ hadResults: hasGlobalSearchResults(searchBefore) });
        }
        abortInFlightConsoleObjectRequests();
        abortReferenceSearches();
        cancelActiveNavigation();
    } else if (type === "GLOBAL-SEARCH/SEARCH"){
        recordGlobalSearchQuery();
        dispatch({
            type: "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEARCH",
            payload: {
                searchTerm: payload.searchTerm,
                limit: CONSOLE_OBJECTS_PREVIEW_LIMIT,
                ...(Array.isArray(payload.objectTypes) ? { objectTypes: payload.objectTypes } : {})
            }
        })
        if(isNcl()){
            dispatch({
                type: "GLOBAL-SEARCH/DOCUMENTATION/SEARCH",
                payload: {
                    searchTerm: payload.searchTerm,
                    limit: CONSOLE_OBJECTS_PREVIEW_LIMIT
                }
            })
            dispatch({
                type: "GLOBAL-SEARCH/KNOWLEDGE-BASE/SEARCH",
                payload: {
                    searchTerm: payload.searchTerm,
                    limit: CONSOLE_OBJECTS_PREVIEW_LIMIT
                }
            })
        }
    } else if (type === "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL"){
        const searchTerm = payload?.searchTerm || getState().globalSearch.searchTerm;
        const organizationId = getState().tenancy.selectedOrgId;

        if(!searchTerm || !organizationId){
            if(searchTerm){
                dispatchMissingOrganization(dispatch, getState, action);
            }
            return response;
        }

        abortInFlightConsoleObjectRequests();
        const controller = new AbortController();
        seeAllAbortController = controller;

        return handleApiAction(action, () => {
            const release = () => {
                if(seeAllAbortController === controller){
                    seeAllAbortController = null;
                }
            };

            const objectTypes = Array.isArray(payload?.objectTypes) ? payload.objectTypes : DEFAULT_ONTAP_OBJECT_TYPES;
            const limit = CONSOLE_OBJECTS_TYPE_SEE_ALL_LIMIT;

            if (!objectTypes.length) {
                return apiClient.get(getConsoleObjectsUrl(organizationId), {
                    params: getConsoleObjectsQuery(searchTerm, limit, []),
                    signal: controller.signal
                }).then(ans => {
                    release();
                    const mapped = mapUnifiedSearchResponse(ans.data);
                    return {
                        data: {
                            results: mapped.results || [],
                            count: _.isNumber(mapped.count) ? mapped.count : 0,
                            partialError: false
                        }
                    };
                }).catch(error => {
                    release();
                    if (axios.isCancel(error)) {
                        return { data: "duplicate call" };
                    }
                    throw error;
                });
            }

            return Promise.allSettled(objectTypes.map(objectType => apiClient.get(getConsoleObjectsUrl(organizationId), {
                params: getConsoleObjectsQuery(searchTerm, limit, [objectType]),
                signal: controller.signal
            }).then(ans => mapUnifiedSearchResponse(ans.data)))).then(settled => {
                release();
                const rejected = settled.filter(item => item.status === "rejected");
                if (rejected.some(item => axios.isCancel(item.reason))) {
                    return { data: "duplicate call" };
                }
                const fulfilled = settled.filter(item => item.status === "fulfilled").map(item => item.value);
                if (!fulfilled.length) {
                    throw rejected[0]?.reason || new Error("GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL failed");
                }
                if (rejected.length) {
                    console.error("GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL partial failure", rejected.map(item => item.reason));
                }
                const results = _.uniqBy(
                    fulfilled.flatMap(item => item.results),
                    item => item.id || `${item.objectType}:${item.name}`
                );
                const failedObjectTypes = settled.flatMap((item, index) => item.status === "rejected" ? [objectTypes[index]] : []);
                return {
                    data: {
                        results,
                        count: seeAllResultCount(fulfilled),
                        partialError: failedObjectTypes.length > 0,
                        ...(failedObjectTypes.length ? { failedObjectTypes } : {})
                    }
                };
            });
        });
    } else if (type === "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEARCH"){
        const { searchTerm, limit, objectTypes } = payload;
        const organizationId = getState().tenancy.selectedOrgId;

        if(!searchTerm || !organizationId){
            if(searchTerm){
                dispatchMissingOrganization(dispatch, getState, action);
            }
            return response;
        }

        abortInFlightConsoleObjectRequests();

        return handleApiAction(action, () => {
            const controller = new AbortController();
            consoleObjectsAbortController = controller;

            const release = () => {
                if(consoleObjectsAbortController === controller){
                    consoleObjectsAbortController = null;
                }
            };

            return apiClient.get(getConsoleObjectsUrl(organizationId), {
                params: getConsoleObjectsQuery(searchTerm, limit, objectTypes),
                signal: controller.signal
            }).then(ans => {
                release();
                return ans
            }).catch((e) => {
                release();
                if (axios.isCancel(e)){
                    return {data: "duplicate call"}
                } else {
                    throw e
                }
            })
        })
    } else if (type === "GLOBAL-SEARCH/DOCUMENTATION/SEARCH" || type === "GLOBAL-SEARCH/KNOWLEDGE-BASE/SEARCH"){
        const { searchTerm, limit } = payload;
        const organizationId = getState().tenancy.selectedOrgId;
        const isDocumentation = type === "GLOBAL-SEARCH/DOCUMENTATION/SEARCH";

        if(!searchTerm || !organizationId){
            if(searchTerm){
                dispatchMissingOrganization(dispatch, getState, action);
            }
            return response;
        }

        return handleApiAction(action, () => fetchSearchResults(
            organizationId,
            isDocumentation ? getDocumentationSearchQuery(searchTerm, limit) : getKnowledgeBaseSearchQuery(searchTerm, limit),
            () => isDocumentation ? documentationAbortController : knowledgeBaseAbortController,
            controller => {
                if (isDocumentation) {
                    documentationAbortController = controller;
                } else {
                    knowledgeBaseAbortController = controller;
                }
            }
        ));
    }

    if (type.startsWith("GLOBAL-SEARCH/SELECT-SEARCH/")) {
        trackGlobalSearchResultClicked({
            category: globalSearchResultCategory(type),
            objectType: payload?.objectType,
            rank: action.rank,
            matchType: matchTypeForLabel(payload?.name || payload?.label, searchBefore?.searchTerm),
            inputMethod: action.inputMethod
        });
    }

    return response;
}
